import { useEffect, useMemo } from "react";
import {
  PrimaryAvatarNameAndHAPId,
  PrimaryCollapsibleList,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import {
  CONSUMER_USER,
  EMPTY_STRING,
  MY_PROFILE_ROUTES,
  SITE_DISPLAY_ROLES,
} from "../../Constants/CommonConstants";
import {
  MY_PROFILE_ACCOUNT_SETTINGS,
  MY_PROFILE_LOGIN_INFORMATION_LABEL,
  MY_PROFILE_LOGIN_INFORMATION_LINK,
  MY_PROFILE_PERSONAL_DETAILS_LABEL,
  MY_PROFILE_PERSONAL_DETAILS_LINK,
  MY_PROFILE_SECURITY_PREFERENCES_LABEL,
  MY_PROFILE_SECURITY_PREFERENCES_LINK,
  MY_PROFILE_SITE_ACCESS_LABEL,
  MY_PROFILE_SITE_ACCESS_LINK,
} from "../../Constants/MyProfileSideNav";
import { getMyProfile } from "../../Features/MyProfileSlice";
import { manageAuthorizationSelector } from "../../Selectors/AuthorizationSelector";
import { getFullName, getInitials } from "../../Services/CommonServices";
import { StyledMyProfileNavigationStack } from "../../Styles/MyProfile/MyProfile";

const MyProfileSideNav = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const intl = useIntl();

  const location = useLocation();

  const { hapId, firstName, lastName, scopes } = useSelector((state) =>
    manageAuthorizationSelector(state)
  );

  const selectedPage = useMemo(() => {
    if (!location.pathname) {
      return EMPTY_STRING;
    }
    if (location.pathname.includes(MY_PROFILE_ROUTES.loginInformation)) {
      return MY_PROFILE_ROUTES.loginInformation;
    }
    if (location.pathname.includes(MY_PROFILE_ROUTES.securityPreferences)) {
      return MY_PROFILE_ROUTES.securityPreferences;
    }
    if (location.pathname.includes(MY_PROFILE_ROUTES.siteAccess)) {
      return MY_PROFILE_ROUTES.siteAccess;
    }
    return MY_PROFILE_ROUTES.accountSettings;
  }, [location]);

  const getAvatarLabel = useMemo(() => {
    return `${getInitials(firstName)}${getInitials(lastName)}`;
  }, [firstName, lastName]);

  const getSideNavList = () => {
    const sideNavItems = [
      {
        id: MY_PROFILE_PERSONAL_DETAILS_LINK,
        label: intl.formatMessage({
          id: MY_PROFILE_PERSONAL_DETAILS_LABEL,
        }),
        selected: selectedPage === MY_PROFILE_ROUTES.accountSettings,
        handleClick: () => {
          navigate(MY_PROFILE_ROUTES.accountSettings);
        },
      },
      {
        id: MY_PROFILE_LOGIN_INFORMATION_LINK,
        label: intl.formatMessage({
          id: MY_PROFILE_LOGIN_INFORMATION_LABEL,
        }),
        selected: selectedPage === MY_PROFILE_ROUTES.loginInformation,
        handleClick: () => {
          navigate(MY_PROFILE_ROUTES.loginInformation);
        },
      },
      {
        id: MY_PROFILE_SECURITY_PREFERENCES_LINK,
        label: intl.formatMessage({
          id: MY_PROFILE_SECURITY_PREFERENCES_LABEL,
        }),
        selected: selectedPage === MY_PROFILE_ROUTES.securityPreferences,
        handleClick: () => {
          navigate(MY_PROFILE_ROUTES.securityPreferences);
        },
      },
    ];

    if (SITE_DISPLAY_ROLES.includes(scopes)) {
      sideNavItems.push({
        id: MY_PROFILE_SITE_ACCESS_LINK,
        label: intl.formatMessage({
          id: MY_PROFILE_SITE_ACCESS_LABEL,
        }),
        selected: selectedPage === MY_PROFILE_ROUTES.siteAccess,
        handleClick: () => {
          navigate(MY_PROFILE_ROUTES.siteAccess);
        },
      });
    }
    return sideNavItems;
  };

  useEffect(() => {
    dispatch(getMyProfile());
  }, [dispatch]);

  return (
    <StyledMyProfileNavigationStack>
      <PrimaryAvatarNameAndHAPId
        initials={getAvatarLabel}
        name={getFullName(firstName, lastName)}
        hapId={hapId && scopes?.includes(CONSUMER_USER) ? hapId : null}
      />
      <PrimaryCollapsibleList
        id={MY_PROFILE_ACCOUNT_SETTINGS}
        sideBorder={{ xs: true, md: false }}
        primaryNavigationText={MY_PROFILE_ACCOUNT_SETTINGS}
        listItemProps={getSideNavList()}
      />
    </StyledMyProfileNavigationStack>
  );
};

export default MyProfileSideNav;
